import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import { inr, num, day, apiError } from '../utils/format';
import { useAuth } from '../context/AuthContext';
import { useDataTable, TableToolbar, TablePager } from '../components/DataTable';

export const WEEK_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const AddEmployeeModal = ({ locations, onClose, onSaved }) => {
  const [f, setF] = useState({ name: '', code: '', phone: '', email: '', locationId: '', dob: '', address: '', designation: '', department: '', monthlySalary: '', monthlyOffAllowance: 4, joinDate: '', offBalance: 0 });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const [busy, setBusy] = useState(false); const [err, setErr] = useState('');
  const submit = async () => {
    if (!f.name.trim()) { setErr('Name is required'); return; }
    setBusy(true); setErr('');
    try {
      const { data } = await api.post('/employees', { ...f, locationId: f.locationId ? Number(f.locationId) : null, monthlySalary: Number(f.monthlySalary) || 0, offBalance: Number(f.offBalance) || 0 });
      toast.success(`Employee added${data.employee?.code ? ` · ${data.employee.code}` : ''}`); onSaved(data.employee); onClose();
    } catch (e) { setErr(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title="Add employee" onClose={onClose} wide
      footer={<><button className="btn btn-outline" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Add'}</button></>}>
      <div className="row">
        <div className="field" style={{ flex: 2 }}><label>Name *</label><input value={f.name} onChange={(e) => set('name', e.target.value)} autoFocus /></div>
        <div className="field"><label>Code <span className="muted small">(auto if blank)</span></label><input value={f.code} onChange={(e) => set('code', e.target.value)} placeholder="EMP0001" /></div>
        <div className="field"><label>Location / branch</label><select value={f.locationId} onChange={(e) => set('locationId', e.target.value)}><option value="">— none —</option>{(locations || []).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</select></div>
      </div>
      <div className="row">
        <div className="field"><label>Phone</label><input value={f.phone} onChange={(e) => set('phone', e.target.value)} /></div>
        <div className="field"><label>Email</label><input value={f.email} onChange={(e) => set('email', e.target.value)} /></div>
        <div className="field"><label>Date of birth</label><input type="date" value={f.dob} onChange={(e) => set('dob', e.target.value)} /></div>
        <div className="field"><label>Designation</label><input value={f.designation} onChange={(e) => set('designation', e.target.value)} /></div>
        <div className="field"><label>Department</label><input value={f.department} onChange={(e) => set('department', e.target.value)} /></div>
      </div>
      <div className="field"><label>Address (as per address proof)</label><textarea rows={2} value={f.address} onChange={(e) => set('address', e.target.value)} /></div>
      <div className="row">
        <div className="field"><label>Monthly salary (₹)</label><input type="number" min="0" value={f.monthlySalary} onChange={(e) => set('monthlySalary', e.target.value)} /></div>
        <div className="field"><label>Leaves per month</label><input type="number" min="0" value={f.monthlyOffAllowance} onChange={(e) => set('monthlyOffAllowance', e.target.value)} /></div>
        <div className="field"><label>Join date</label><input type="date" value={f.joinDate} onChange={(e) => set('joinDate', e.target.value)} /></div>
        <div className="field"><label>Opening off balance</label><input type="number" step="0.5" value={f.offBalance} onChange={(e) => set('offBalance', e.target.value)} /></div>
      </div>
      {err && <p className="form-error">{err}</p>}
    </Modal>
  );
};

// ── Download / upload employees (all fields) ───────────────────
const EMP_EXPORT_COLS = [
  ['Id', 'id'], ['Code', 'code'], ['Name', 'name'], ['Phone', 'phone'], ['Email', 'email'],
  ['Designation', 'designation'], ['Department', 'department'], ['Location', 'location'],
  ['DOB', 'dob'], ['Join Date', 'joinDate'], ['End Date', 'exitDate'], ['Monthly Salary', 'monthlySalary'],
  ['Leaves Per Month', 'monthlyOffAllowance'], ['Off Balance', 'offBalance'], ['Status', 'status'],
  ['Address', 'address'], ['Aadhaar', 'aadhaarNumber'], ['PAN', 'panNumber'],
  ['Bank Name', 'bankName'], ['Bank Account', 'bankAccount'], ['IFSC', 'ifsc'], ['UPI Id', 'upiId'], ['Notes', 'notes'],
];
const EMP_HEADER_MAP = {
  id: 'id', empid: 'id', code: 'code', employeecode: 'code', name: 'name', employeename: 'name',
  phone: 'phone', mobile: 'phone', email: 'email', designation: 'designation', role: 'designation',
  department: 'department', dept: 'department', location: 'location', branch: 'location', store: 'location',
  dob: 'dob', dateofbirth: 'dob', joindate: 'joinDate', doj: 'joinDate',
  exitdate: 'exitDate', enddate: 'exitDate', dateofexit: 'exitDate', dol: 'exitDate', lastworkingday: 'exitDate', leavingdate: 'exitDate',
  monthlysalary: 'monthlySalary', salary: 'monthlySalary',
  leavespermonth: 'monthlyOffAllowance', leaves: 'monthlyOffAllowance', monthlyoffallowance: 'monthlyOffAllowance', offallowance: 'monthlyOffAllowance',
  offbalance: 'offBalance', openingoffbalance: 'offBalance', status: 'status', address: 'address',
  aadhaar: 'aadhaarNumber', aadhaarnumber: 'aadhaarNumber', pan: 'panNumber', pannumber: 'panNumber',
  bankname: 'bankName', bankaccount: 'bankAccount', accountnumber: 'bankAccount', ifsc: 'ifsc',
  upi: 'upiId', upiid: 'upiId', notes: 'notes',
};
const normHeader = (h) => String(h || '').toLowerCase().replace(/[^a-z0-9]/g, '');
function parseCSV(text) {
  const rows = []; let row = []; let field = ''; let i = 0; let q = false;
  while (i < text.length) {
    const c = text[i];
    if (q) { if (c === '"') { if (text[i + 1] === '"') { field += '"'; i += 2; continue; } q = false; i++; continue; } field += c; i++; continue; }
    if (c === '"') { q = true; i++; continue; }
    if (c === ',') { row.push(field); field = ''; i++; continue; }
    if (c === '\r') { i++; continue; }
    if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; i++; continue; }
    field += c; i++;
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  return rows;
}
const csvCell = (v) => { const s = v == null ? '' : String(v); return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
const exportEmployeesCSV = (list) => {
  const lines = [EMP_EXPORT_COLS.map(([h]) => h).join(',')];
  for (const e of list) lines.push(EMP_EXPORT_COLS.map(([, k]) => csvCell(k === 'location' ? (e.location?.name || '') : (e[k] ?? ''))).join(','));
  const blob = new Blob(['﻿' + lines.join('\r\n') + '\r\n'], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = `employees-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
};

const EmployeesImportModal = ({ onClose, onDone }) => {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const onFile = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setFileName(file.name); setError(''); setResult(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const grid = parseCSV(String(reader.result));
        if (grid.length < 2) { setError('File has no data rows.'); setRows([]); return; }
        const headers = grid[0].map((h) => EMP_HEADER_MAP[normHeader(h)] || null);
        if (!headers.includes('name') && !headers.includes('id')) { setError('Need a Name or Id column.'); setRows([]); return; }
        const out = [];
        for (let r = 1; r < grid.length; r++) {
          const cells = grid[r];
          if (!cells.length || cells.every((c) => !String(c).trim())) continue;
          const obj = {}; headers.forEach((field, idx) => { if (field) obj[field] = String(cells[idx] || '').trim(); });
          if (!obj.name && !obj.id) continue;
          out.push(obj);
        }
        setRows(out); if (!out.length) setError('No data rows found.');
      } catch (err) { setError('Could not parse file: ' + err.message); setRows([]); }
    };
    reader.readAsText(file);
  };

  const submit = async () => {
    setBusy(true); setError(''); setResult(null);
    try {
      const { data } = await api.post('/employees/import', { rows });
      setResult(data.result);
      toast.success(`Imported — ${data.result.created} new, ${data.result.updated} updated`);
      onDone && onDone();
    } catch (err) { setError(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Modal title="Upload employees" onClose={onClose} wide
      footer={<><button className="btn btn-outline" onClick={onClose}>Close</button><button className="btn btn-primary" onClick={submit} disabled={busy || !rows.length}>{busy ? 'Uploading…' : `Upload ${rows.length} rows`}</button></>}>
      <p className="muted small" style={{ marginTop: 0 }}>Upload a <strong>CSV</strong> (one row per employee). Columns: Code, Name, Phone, Email, Designation, Department, Location, DOB, Join Date, Monthly Salary, Leaves Per Month, Off Balance, Status, Address, Aadhaar, PAN, Bank Name, Bank Account, IFSC, UPI Id, Notes. Matches by <strong>Id → Code → Name</strong>; <strong>Location</strong> is the branch name; new employees without a code get one auto-generated. Only columns present are updated.</p>
      <div className="field"><label>CSV file</label><input type="file" accept=".csv,text/csv" onChange={onFile} /></div>
      {fileName && <p className="muted small">{fileName} — <strong>{rows.length}</strong> rows.</p>}
      {rows.length > 0 && (
        <div className="table-wrap" style={{ maxHeight: 220, overflow: 'auto' }}>
          <table className="grid">
            <thead><tr><th>Name</th><th>Code</th><th>Designation</th><th>Location</th><th className="right">Salary</th></tr></thead>
            <tbody>
              {rows.slice(0, 10).map((r, i) => <tr key={i}><td>{r.name}</td><td className="muted">{r.code}</td><td>{r.designation}</td><td>{r.location}</td><td className="right">{r.monthlySalary}</td></tr>)}
              {rows.length > 10 && <tr><td colSpan={5} className="muted">…and {rows.length - 10} more</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {result && <p className="mt"><strong>Done.</strong> {result.created} new, {result.updated} updated{result.skipped ? `, ${result.skipped} skipped` : ''}{result.errors?.length ? `, ${result.errors.length} issue(s)` : ''}.</p>}
      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};

const Employees = () => {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]); const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false); const [showAll, setShowAll] = useState(false);
  const [importing, setImporting] = useState(false); const [locations, setLocations] = useState([]);

  const load = () => { setLoading(true); api.get(`/employees${showAll ? '?all=1' : ''}`).then(({ data }) => setList(data.employees || [])).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false)); };
  useEffect(load, [showAll]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { api.get('/locations').then(({ data }) => setLocations(data.locations || [])).catch(() => {}); }, []);

  const byBranch = useMemo(() => [...list].sort((a, b) => {
    const la = a.location?.name || '';
    const lb = b.location?.name || '';
    if (la !== lb) {
      if (!la) return 1;
      if (!lb) return -1;
      return la.localeCompare(lb, undefined, { numeric: true });
    }
    return String(a.name || '').localeCompare(String(b.name || ''), undefined, { numeric: true });
  }), [list]);

  const dt = useDataTable(byBranch, {
    searchPlaceholder: 'Search name, code, role, phone…',
    searchKeys: ['name', 'code', 'designation', 'phone', 'email'],
    filters: [
      { key: 'department', label: 'Department', value: (e) => e.department },
      { key: 'designation', label: 'Designation', value: (e) => e.designation },
      { key: 'branch', label: 'Branch', value: (e) => e.location?.name },
      { key: 'status', label: 'Status', value: (e) => (e.status === 'active' ? 'Active' : 'Inactive') },
    ],
  });

  return (
    <div>
      <TableToolbar dt={dt} right={<>
        <label className="chk"><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> Incl. inactive</label>
        <button className="btn btn-outline" onClick={() => exportEmployeesCSV(list)} disabled={!list.length} title="Download employees as CSV to edit and re-upload">⭳ Download CSV</button>
        {can('employees:write') && <button className="btn btn-outline" onClick={() => setImporting(true)}>⭱ Upload CSV</button>}
        {can('employees:write') && <button className="btn btn-primary" onClick={() => setAdding(true)}>+ Add Employee</button>}
      </>} />
      {loading ? <div className="spinner" /> : (
        <div className="table-wrap">
          <table className="grid">
            <thead><tr><th>Name</th><th>Code</th><th>Designation</th><th>Branch</th><th className="right">Salary</th><th className="right">Off bal.</th><th className="right">Loan due</th><th>Status</th></tr></thead>
            <tbody>
              {dt.rows.map((e) => (
                <tr key={e.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/employees/${e.id}`)}>
                  <td><Link to={`/employees/${e.id}`} className="link-strong" onClick={(ev) => ev.stopPropagation()}>{e.name}</Link></td>
                  <td className="muted">{e.code || '—'}</td>
                  <td>{e.designation || '—'}</td>
                  <td>{e.location?.name || '—'}</td>
                  <td className="right">{inr(e.monthlySalary)}</td>
                  <td className="right">{num(e.offBalance, 1)}</td>
                  <td className="right">{e.loanOutstanding > 0 ? inr(e.loanOutstanding) : '—'}</td>
                  {/* A leaver's date is the useful half of "inactive": it says
                      which months still count them. */}
                  <td>{e.status === 'active'
                    ? <span className="badge badge-green">Active</span>
                    : <span className="badge badge-gray" title={e.exitDate ? `Last worked ${day(e.exitDate)}` : 'No end date recorded — open them and set one'}>
                        {e.exitDate ? `Left ${day(e.exitDate)}` : 'Inactive · no end date'}
                      </span>}
                  </td>
                </tr>
              ))}
              {dt.rows.length === 0 && <tr><td colSpan={8} className="empty">{dt.isFiltered ? 'No matches.' : 'No employees yet.'}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {!loading && <TablePager dt={dt} />}
      {adding && <AddEmployeeModal locations={locations} onClose={() => setAdding(false)} onSaved={load} />}
      {importing && <EmployeesImportModal onClose={() => setImporting(false)} onDone={load} />}
    </div>
  );
};

export default Employees;
